import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAtom } from "jotai";
import { useAuth } from "common/hooks";
import { scoutBadgeFromResponse } from "common/types/user";

import type { LearningCountKey, LearningItemState } from "./constants";
import {
  learningHubViewedAtom,
  type LearningHubViewedLessons,
  viewerKey,
} from "./state";

const NO_LESSONS_VIEWED: LearningHubViewedLessons = {};

/**
 * Each lesson's circle — native's `useLearningProgress`, read the same way:
 *
 * - **completed** when `sync-user`'s `scout_badge` says the lesson's action is
 *   done (a task flag, or a count above zero);
 * - otherwise **seen** once the lesson has been opened by this viewer;
 * - otherwise **not-seen**.
 *
 * Inherits native's caveat: the backend reports every task pending and every
 * count 0 while the Scout competition flag is off, so no row shows its check
 * then.
 */
export function useLearningProgress() {
  const { user } = useAuth();
  const [viewedByViewer, setViewedByViewer] = useAtom(learningHubViewedAtom);

  // `useAuth` hydrates late: undefined means "no answer yet", null signed out.
  const authUser = user as { id?: number | string; scout_badge?: unknown } | null | undefined;
  const viewer = viewerKey(authUser?.id ?? null, authUser !== undefined);
  const viewed = (viewer ? viewedByViewer[viewer] : null) ?? NO_LESSONS_VIEWED;

  const badge = useMemo(
    () => scoutBadgeFromResponse(authUser?.scout_badge),
    [authUser?.scout_badge],
  );

  const stateFor = useCallback(
    (lesson: {
      id: string;
      actionTaskId?: string;
      actionCountKey?: LearningCountKey;
    }): LearningItemState => {
      if (
        lesson.actionTaskId &&
        badge?.tasks.some(
          (task) => task.id === lesson.actionTaskId && task.isCompleted,
        )
      ) {
        return "completed";
      }
      if (lesson.actionCountKey && (badge?.counts[lesson.actionCountKey] ?? 0) > 0) {
        return "completed";
      }
      return viewed[lesson.id] ? "seen" : "not-seen";
    },
    [badge, viewed],
  );

  /** Records a lesson as opened. A no-op until the viewer is known. */
  const markViewed = useCallback(
    (lessonId: string) => {
      if (!viewer) return;
      setViewedByViewer((current) => {
        const lessons = current[viewer] ?? NO_LESSONS_VIEWED;
        if (lessons[lessonId]) return current;
        return { ...current, [viewer]: { ...lessons, [lessonId]: true } };
      });
    },
    [setViewedByViewer, viewer],
  );

  /** False until `useAuth` has answered; see `useLessonOpened`. */
  const isReady = viewer !== null;

  return { stateFor, markViewed, isReady };
}


type LessonRef = {
  id: string;
  actionTaskId?: string;
  actionCountKey?: LearningCountKey;
};

/**
 * Runs `onOpen` once each time `lesson` is opened, with its state as it was
 * before this visit, then records the visit.
 *
 * Waits for the viewer: until `useAuth` answers, `stateFor` knows neither
 * the reading history nor the badge, so every lesson would read as not-seen.
 * Pass `undefined` while the lesson is not on screen; the next lesson passed
 * counts as a fresh opening.
 */
export function useLessonOpened(
  lesson: LessonRef | undefined,
  onOpen: (state: LearningItemState) => void,
) {
  const { stateFor, markViewed, isReady } = useLearningProgress();
  const openedId = useRef<string | null>(null);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const lessonRef = useRef(lesson);
  lessonRef.current = lesson;
  const lessonId = lesson?.id;

  useEffect(() => {
    const current = lessonRef.current;
    if (!current) {
      openedId.current = null;
      return;
    }
    if (!isReady || openedId.current === current.id) return;
    openedId.current = current.id;
    onOpenRef.current(stateFor(current));
    markViewed(current.id);
  }, [isReady, lessonId, stateFor, markViewed]);
}
